'use client';
import { ReactNode, useId } from 'react';
import Image from 'next/image';
import cn from 'classnames';

import Text from '@/components/common/Text/Text';
import ChevronDownBlack from 'public/img/icon/chevron-down-black.svg';
import './CollapsibleSection.style.scss';

type CollapsibleSectionProps = {
  className?: string;
  /** Section content */
  children: ReactNode;
  /** Title of the section */
  title: ReactNode;
  /** Whether the section is currently expanded */
  open?: boolean;
  /** Callback to run when the user asks to open/close the section */
  onRequestChange?: (open: boolean) => void;
  /** Prevents the section from being opened, e.g. a later step not yet reached */
  disabled?: boolean;
  /** Optional trailing header content, e.g. an "Edit" affordance */
  headerAside?: ReactNode;
  /**
   * Recap of what the section contains, shown only while it is collapsed. Lets
   * a completed section stay closed without hiding what was entered — the
   * reason to reopen it is usually to check one value, not to edit.
   */
  summary?: ReactNode;
  /**
   * Root gets this id; the toggle gets `${testId}-toggle` and the content
   * `${testId}-content`, so tests can target a section without depending on
   * DOM structure.
   */
  testId?: string;
};

/**
 * A collapsible section that animates to its natural (auto) height.
 *
 * Unlike `AnimatedAccordion`, this takes no `expandedHeight`: it uses the CSS
 * `grid-template-rows: 0fr -> 1fr` technique so content of genuinely variable
 * height — inline validation errors appearing/disappearing, conditionally
 * rendered field groups — is never clipped and never leaves dead space.
 *
 * IMPORTANT — the content stays MOUNTED when collapsed (hidden via CSS + made
 * `inert`), it is never conditionally unmounted. This is load-bearing:
 * **Stripe Elements wipe their input the moment they unmount**, so if the
 * billing section collapsed by unmounting, the member's entered card number
 * would be gone. Keep the content mounted and hidden; do not "optimize" this
 * into a conditional render.
 */
function CollapsibleSection(props: CollapsibleSectionProps) {
  const {
    className = '',
    children,
    title,
    open = false,
    onRequestChange,
    disabled = false,
    headerAside,
    summary,
    testId
  } = props;

  const generatedId = useId();
  const idBase = testId ?? generatedId;
  const contentId = `${idBase}-content`;

  const handleRequestChange = () => onRequestChange?.(!open);

  // Collapsed-but-mounted content must be `inert` so keyboard users cannot tab
  // into invisible fields and it is removed from the a11y tree. `inert` is not
  // in React 18.2's JSX prop types, so spread it through a cast.
  const inertProps = (!open ? { inert: '' } : {}) as Record<string, string>;

  return (
    <div
      className={cn('CollapsibleSection', className, { open })}
      data-testid={testId}
    >
      <div className='CollapsibleSection-header'>
        <button
          type='button'
          className={cn('CollapsibleSection-toggle', { cursor: !disabled })}
          onClick={handleRequestChange}
          disabled={disabled}
          aria-expanded={open}
          aria-disabled={disabled || undefined}
          aria-controls={contentId}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          {typeof title === 'string' ? (
            <Text variant='title-xs'>{title}</Text>
          ) : (
            <>{title}</>
          )}
          <Image
            src={ChevronDownBlack}
            alt=''
            className={cn('CollapsibleSection-arrow', { open })}
          />
        </button>
        {headerAside && (
          <div className='CollapsibleSection-header-aside'>{headerAside}</div>
        )}
      </div>

      {/*
        Rendered only when collapsed: expanded, the fields themselves are the
        better source, and showing both would just be the same values twice.
      */}
      {!open && summary && (
        <div
          className='CollapsibleSection-summary'
          data-testid={testId ? `${testId}-summary` : undefined}
        >
          {summary}
        </div>
      )}

      <div
        id={contentId}
        className={cn('CollapsibleSection-content', { open })}
        aria-hidden={!open}
        data-testid={testId ? `${testId}-content` : undefined}
        {...inertProps}
      >
        <div className='CollapsibleSection-content-inner'>{children}</div>
      </div>
    </div>
  );
}

export default CollapsibleSection;
